import axios from './axios';
import { GOOGLE_API_KEY } from '../config';

export default {
    auth: {
        login: (data) => axios.post('auth/login', data),
        setup: () => axios.post('setup', {}),
        get: () => axios.get('auth'),
        settings: {
            get: (userId) => axios.get(`/auth/${userId}/settings`),
            update: (user) => axios.put(`/auth/${user.id}/settings`, user),
        },
        password: {
            update: (data) => axios.put('auth/password', data),
            reset: (data) => axios.put('auth/password/reset', data),
            sendRecoveryEmail: (data) => axios.post('auth/password/recovery', data)
        }
    },
    meta: {
        get: () => axios.post('meta/'),
    },
    locales: {
        get: (ns, chainId, branchId) => axios.get(`locales/${ns}?chainId=${chainId || ''}&branchId=${branchId || ''}`),
        update: (ns, chainId, branchId, updates) => axios.put(`locales/${ns}`, { chainId, branchId, updates }),
        create: (ns, row) => axios.post(`locales/${ns}`, row),
    },
    brands: {
        getBrands: (chainId, restId) => axios.get('brands'.concat(restId || chainId ? `?${chainId ? 'chainId' : 'restId'}=${chainId || restId}` : '')),
        get: (brandKey, platform) => axios.get(`brands/${brandKey}?${platform && `platform=${platform}` || ''}`),
        create: (brand) => axios.post('brands', brand),
        createAsset: (brandKey, assetKey, data) => axios.post(`brands/${brandKey}/asset/${assetKey}`, data),
        deleteAsset: (brandKey, assetKey) => axios.delete(`brands/${brandKey}/asset/${assetKey}`),
        updateTheme: (brandKey, data, platform) => axios.put(`brands/${brandKey}/theme?platform=${platform}`, data)
    },
    restaurant: {
        getAll: (ids = [], deletedOnly) => axios.get(`restaurants?deletedOnly=${deletedOnly || ''}${ids.length ? `&ids=${ids.join(',')}` : ''}`),
        create: (rest) => axios.post(`restaurants`, rest),
        update: (rest) => axios.put(`restaurants/${rest.id}`, rest),
        delete: (restId) => axios.delete(`restaurants/${restId}`),
        get: (restId) => ({
            orders: {
                get: orderId => axios.get(`restaurants/${restId}/orders/${orderId}`),
                getAll: (startDate, endDate) => axios.get(`restaurants/${restId}/orders${startDate ? `?startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`),
                close: (orderGuid) => axios.post(`restaurants/${restId}/orders/close`, { orderGuid }),
                cancel: (orderGuid, reason, details, notifyUser) => axios.post(`restaurants/${restId}/orders/cancel`, { orderGuid, reason, details, notifyUser }),
                creditValidation: (paymentId, amount) => axios.post(`restaurants/${restId}/orders/credit/validate`, { paymentId, amount }),
                credit: (creditForm) => axios.post(`restaurants/${restId}/orders/credit`, creditForm),
                resend: (orderId) => axios.post(`restaurants/${restId}/orders/resend`, { orderId }),
                sendTestOrder: (checkout, cart, shouldSendPayments) => axios.post(`restaurants/${restId}/orders/test?shouldSendPayments=${shouldSendPayments || ''}`, { checkout, cart }),
                subscriptions: {
                    getAll: () => axios.get(`restaurants/${restId}/ordersubscriptions`),
                    create: (sub) => axios.post(`restaurants/${restId}/ordersubscriptions`, sub),
                    delete: (subId) => axios.delete(`restaurants/${restId}/ordersubscriptions/${subId}`),
                },
                receipt: (orderId, language, replacementVoucher) => axios.get(`restaurants/${restId}/receipt/${orderId}?language=${language}&replacementVoucher=${replacementVoucher || ''}`)
            },
            marketingCampaigns: {
                getAll: (ids = []) => axios.get(`restaurants/${restId}/marketingCampaigns${ids.length ? `?ids=${ids.join(',')}&restId=${restId}` : `?restId=${restId}`}`),
                create: (campaign) => axios.post(`restaurants/${restId}/marketingCampaigns`, campaign),
                update: (campaign) => axios.put(`restaurants/${restId}/marketingCampaigns/${campaign.id}`, campaign),
                delete: (campaignId) => axios.delete(`restaurants/${restId}/marketingCampaigns/${campaignId}`),
            },
            openingHours: {
                fetchFromGoogle: (placeId) => axios.get(`restaurants/${restId}/openinghours/external?placeId=${placeId}`),
                update: (hours) => axios.put(`restaurants/${restId}/openinghours`, { hours })
            },
            deliveryHours: {
                update: (hours) => axios.put(`restaurants/${restId}/deliveryhours`, { hours })
            },
            deliveryAreas: {
                update: (areas) => axios.put(`restaurants/${restId}/deliveryareas`, { areas })
            },
            loyalty: {
                getGroups: () => axios.get(`restaurants/${restId}/loyalty/groups`),
                createGroup: (group) => axios.post(`restaurants/${restId}/loyalty/groups`, group),
                deleteGroup: (groupId) => axios.delete(`restaurants/${restId}/loyalty/groups/${groupId}`),
                createMember: (groupId, member) => axios.post(`restaurants/${restId}/loyalty/groups/${groupId}/members`, member),
                deleteMember: (groupId, memberId) => axios.delete(`restaurants/${restId}/loyalty/groups/${groupId}/members/${memberId}`),
                getRewards: () => axios.get(`restaurants/${restId}/loyalty/rewards`),
                createReward: (reward) => axios.post(`restaurants/${restId}/loyalty/rewards`, reward),
                deleteReward: (rewardId) => axios.delete(`restaurants/${restId}/loyalty/rewards/${rewardId}`),
                sendReward: (data) => axios.post(`restaurants/${restId}/loyalty/rewards/send`, data),
            },
            config: {
                getConfig: () => axios.get(`restaurants/${restId}/config`),
                createPosTerminal: (data) => axios.post(`restaurants/${restId}/config/pos`, data)
            },
            salesData: (salesSystemId) => axios.get(`sales/${salesSystemId}`),
            metabase: {
                get: () => axios.get(`restaurants/${restId}/metabase`),
                set: () => axios.post(`restaurants/${restId}/metabase`),
            },
            receipt: {
                getConfig: () => axios.get(`restaurants/${restId}/receipt/config`),
                setConfig: (config) => axios.put(`restaurants/${restId}/receipt/config`, config),
            }
        }),
    },
    company: {
        getAll: (ids = []) => axios.get(`companies${ids.length ? `?ids=${ids.join(',')}` : ''}`),
        create: (company) => axios.post(`companies`, company),
        update: (company) => axios.put(`companies/${company.id}`, company),
        delete: (companyId) => axios.delete(`companies/${companyId}`),
        get: (companyId) => ({
            workers: {
                getAll: () => axios.get(`companies/${companyId}/workers`),
                addWorker: (worker) => axios.post(`companies/${companyId}/workers`, worker),
                lockWorker: (data) => axios.post(`companies/${companyId}/workers/lock`, data),
                revokeWorker: (data) => axios.post(`companies/${companyId}/workers/revoke`, data),
                get: (workerId) => ({
                    permissions: {
                        getAll: () => axios.get(`companies/${companyId}/workers/${workerId}/permissions`),
                        create: (permission) => axios.post(`companies/${companyId}/workers/${workerId}/permissions`, permission),
                        delete: (permissionId) => axios.delete(`companies/${companyId}/workers/${workerId}/permissions/${permissionId}`)
                    }
                })
            },
            budget: {
                groups: {
                    getAll: () => axios.get(`companies/${companyId}/budget/groups`),
                    create: (group) => axios.post(`companies/${companyId}/budget/groups`, group),
                    update: (group) => axios.put(`companies/${companyId}/budget/groups/${group.id}`, group),
                    delete: (groupId) => axios.delete(`companies/${companyId}/budget/groups/${groupId}`)
                },
                calc: (data) => axios.post(`companies/${companyId}/budget/calc`, data),
                load: (data) => axios.post(`companies/${companyId}/budget/load`, data),
                breakdown: (data) => axios.post(`companies/${companyId}/budget/breakdown`, data)
            },
            orders: {
                getAll: () => axios.get(`companies/${companyId}/orders`),
                notify: (orderId) => axios.post(`companies/${companyId}/orders/notify`, { orderId }),
            },
            paymentOptions: () => axios.get(`companies/${companyId}/payment-options`),
        }),
    },
    loyaltyProgram: {
        getAll: (ids = []) => axios.get(`loyaltyPrograms${ids.length ? `?ids=${ids.join(',')}` : ''}`),
        create: (loyaltyProgram) => axios.post(`loyaltyPrograms`, loyaltyProgram),
        update: (loyaltyProgram) => axios.put(`loyaltyPrograms/${loyaltyProgram.id}`, loyaltyProgram),
        delete: (loyaltyProgramId) => axios.delete(`loyaltyPrograms/${loyaltyProgramId}`),
        get: (loyaltyProgramId) => ({
            restaurants: {
                getFull: (restaurantsIds) => axios.post(`loyaltyPrograms/${loyaltyProgramId}/restaurants`, { restaurantsIds }),
                add: (restaurantsIds) => axios.post(`loyaltyPrograms/${loyaltyProgramId}/restaurants/add`, { restaurantsIds }),
                remove: (restaurantId) => axios.delete(`loyaltyPrograms/${loyaltyProgramId}/restaurants/${restaurantId}`)
            },
            members: {
                getFull: (membersIds) => axios.post(`loyaltyPrograms/${loyaltyProgramId}/members`, { membersIds }),
                add: (member) => axios.post(`loyaltyPrograms/${loyaltyProgramId}/members/add`, member),
                remove: (memberId) => axios.delete(`loyaltyPrograms/${loyaltyProgramId}/members/${memberId}`),
            },
            campaigns: {
                getFull: () => axios.post(`loyaltyPrograms/${loyaltyProgramId}/campaigns`),
                create: (campaign) => axios.post(`loyaltyPrograms/${loyaltyProgramId}/campaigns/create`, campaign),
                update: (campaign) => axios.put(`loyaltyPrograms/${loyaltyProgramId}/campaigns/${campaign.id}`, campaign),
                close: (campaignId) => axios.put(`loyaltyPrograms/${loyaltyProgramId}/campaigns/${campaignId}/close`),
                delete: (campaignId) => axios.delete(`loyaltyPrograms/${loyaltyProgramId}/campaigns/${campaignId}`)
            },
            benefits: {
                getFull: () => axios.post(`loyaltyPrograms/${loyaltyProgramId}/benefits`),
                create: (benefit) => axios.post(`loyaltyPrograms/${loyaltyProgramId}/benefits/create`, benefit),
                update: (benefit) => axios.put(`loyaltyPrograms/${loyaltyProgramId}/benefits/${benefit.id}`, benefit),
                delete: (benefitId) => axios.delete(`loyaltyPrograms/${loyaltyProgramId}/benefits/${benefitId}`),
                issue: (benefitId, issue) => axios.post(`loyaltyPrograms/${loyaltyProgramId}/benefits/${benefitId}/issue`, issue),
                issues: {
                    update: (benefitId, issue) => axios.put(`loyaltyPrograms/${loyaltyProgramId}/benefits/${benefitId}/issues/${issue.id}/update`, issue),
                    closeIssue: (benefitId, issueId) => axios.get(`loyaltyPrograms/${loyaltyProgramId}/benefits/${benefitId}/issues/${issueId}/close`),
                }
            }
        })
    },
    menus: {
        create: (restaurantId, label = restaurantId) => axios.post(`menus/create`, { restaurantId }),
        clone: (menuId, restaurantId) => axios.post(`menus/clone`, { menuId, restaurantId }),
        get: (menuId, branchIdForOverride) => ({
            getFull: () => axios.get(`menus/${menuId}?branchIdForOverride=${branchIdForOverride || ''}`),
            editInfo: (lastRefresh = null) => axios.post(`menus/${menuId}/edit-info`, { sign: true, lastRefresh }),
            categories: {
                create: (category) => axios.post(`menus/${menuId}/categories?branchIdForOverride=${branchIdForOverride || ''}`, category),
                update: (category) => axios.put(`menus/${menuId}/categories/${category.id}?branchIdForOverride=${branchIdForOverride || ''}`, category),
                delete: (categoryId) => axios.delete(`menus/${menuId}/categories/${categoryId}?branchIdForOverride=${branchIdForOverride || ''}`),
            },
            items: {
                create: (item) => axios.post(`menus/${menuId}/items?branchIdForOverride=${branchIdForOverride || ''}`, item),
                update: (item) => axios.put(`menus/${menuId}/items/${item.id}?branchIdForOverride=${branchIdForOverride || ''}`, item),
                delete: (itemId) => axios.delete(`menus/${menuId}/items/${itemId}?branchIdForOverride=${branchIdForOverride || ''}`),
            },
            overrides: {
                delete: (branchId, entityType, entityId) => axios.delete(`menus/${menuId}/overrides?entityType=${entityType}&entityId=${entityId}&branchId=${branchId}`)
            },
            prepare: (timezone) => axios.get(`menus/${menuId}/prepare?timezone=${timezone}&branchIdForOverride=${branchIdForOverride}`),
            getTranslations: () => axios.get(`menus/${menuId}/translations`),
            updateTranslations: (dictionary) => axios.put(`menus/${menuId}/translations`, { dictionary }),
            initTranslations: (languages) => axios.post(`menus/${menuId}/translations`, { languages }),
            smartPromotions: {
                fetch: () => axios.get(`menus/${menuId}/smartPromotions?branchIdForOverride=${branchIdForOverride || ''}`),
                create: (upsell) => axios.post(`menus/${menuId}/smartPromotions?branchIdForOverride=${branchIdForOverride || ''}`, upsell),
                update: (upsell) => axios.put(`menus/${menuId}/smartPromotions/${upsell.id}?branchIdForOverride=${branchIdForOverride || ''}`, upsell),
            }
        })
    },
    websites: {
        get: (chainId) => axios.get(`websites/${chainId}`),
        create: (website) => axios.post(`websites`, website),
        update: (website) => axios.put(`websites/${website.id}`, website),
    },
    kiosks: {
        getAll: (restIds = []) => axios.get(`kiosks${restIds.length ? `?restIds=${restIds.join(',')}` : ''}`),
        create: (kiosk) => axios.post(`kiosks`, kiosk),
        update: (kiosk) => axios.put(`kiosks/${kiosk.id}`, kiosk),
        delete: (kioskId) => axios.delete(`kiosks/${kioskId}`),
        generateToken: (kioskId, restId) => axios.post(`kiosks/token`, { kioskId, restId }),
    },
    chains: {
        getAll: (ids = []) => axios.get(`owners${ids.length ? `?ids=${ids.join(',')}` : ''}`),
        create: (chain) => axios.post(`owners`, chain),
        update: (chain) => axios.put(`owners/${chain.id}`, chain),
        delete: (chainId) => axios.delete(`owners/${chainId}`),
        get: (chainId) => ({
            marketingCampaigns: {
                getAll: (ids = []) => axios.get(`owners/${chainId}/marketingCampaigns${ids.length ? `?ids=${ids.join(',')}` : ''}`),
                create: (campaign) => axios.post(`owners/${chainId}/marketingCampaigns`, campaign),
                update: (campaign) => axios.put(`owners/${chainId}/marketingCampaigns/${campaign.id}`, campaign),
                delete: (campaignId) => axios.delete(`owners/${chainId}/marketingCampaigns/${campaignId}`),
            }
        }),
    },
    restaurantTags: {
        getAll: () => axios.get(`tags`),
        create: (tag) => axios.post(`tags/add`, tag),
    },
    dashboardUsers: {
        getAll: () => axios.get(`users/dashboard`),
        create: (user) => axios.post(`users/dashboard`, user),
        update: (user) => axios.put(`users/dashboard/${user.id}`, user),
        delete: (userId) => axios.delete(`users/dashboard/${userId}`)
    },
    images: {
        getCommon: () => axios.get(`/images/common`),
        uploadCommon: (data, mode) => axios.post(`/images/common?mode=${mode}`, data),
        upload: (data, purpose, mode) => axios.post(`/images/${purpose}?mode=${mode}`, data),
        delete: (key) => axios.delete(`/images/${key}`)
    },
    reports: {
        payments: (data) => axios.post(`/reports/orders/payments`, data),
        budget: (data) => axios.post(`/reports/companies/budget`, data),
        campaign: {
            general: (data) => axios.post(`/reports/loyalty-programs/campaign`, data),
            usages: (data) => axios.post(`/reports/loyalty-programs/campaign/usages`, data)
        },
        member: {
            history: (data) => axios.post(`/reports/loyalty-programs/member/history`, data)
        }
    },
    invoices: {
        send: (data) => axios.post(`/invoices`, data),
    },
    deliveryAreasPool: {
        add: (data) => axios.post(`/deliveryareaspool/add`, data),
        search: (tag) => axios.get(`/deliveryareaspool/search?tag=${tag}`),
        external: (tag) => axios.get(`https://nominatim.openstreetmap.org/search/search.php?q=${tag}&format=json&limit=1&polygon_geojson=1`)
    },
    google: {
        address: (search, lang = 'he') => axios.get(`https://maps.googleapis.com/maps/api/geocode/json?&address=${encodeURIComponent(search)}&language=${lang}&key=${GOOGLE_API_KEY}`, {}),
        place: (placeId) => axios.get(`https://maps.googleapis.com/maps/api/place/details/json?placeid=${placeId}&key=${GOOGLE_API_KEY}`, {}),
    }
};
